// Project a Takt train into the DepGraph shape the Ties tab (SequenceView /
// DepTable) renders, so editing the train reuses the SAME ties UI + gantt. The
// caller supplies the per-element day positions (static board positions, or the
// forward-pass simulation) and the section's WORKDAY calendar, so the dates the
// table shows + the gantt positions are real workdays. Keyed by carId, so the
// tab's edit intents map straight onto the train-dep helpers (no id translation).
import type { DepGraph, DepGraphActivity, DepGraphEdge } from "../../../../models";
import type { TrainState } from "../../../../models";

/** Map a 0-based day index onto the section's workday calendar (clamped). */
function dayToISO(calendar: string[], day: number): string | null {
  if (calendar.length === 0) return null;
  const i = Math.max(0, Math.min(Math.round(day), calendar.length - 1));
  return (calendar[i] ?? "").slice(0, 10) || null;
}

export function buildTrainGraph(
  train: TrainState,
  codeName: Record<string, string>,
  codeColors: Record<string, string>,
  posByCar: Map<string, { startDay: number; finishDay: number }>,
  calendar: string[],
): { graph: DepGraph; boardDates: Map<string, { start: string | null; finish: string | null }> } {
  const boardDates = new Map<string, { start: string | null; finish: string | null }>();
  const activities: Record<string, DepGraphActivity> = {};
  for (const car of train.cars) {
    const pos = posByCar.get(car.carId) ?? { startDay: 0, finishDay: car.durationDays };
    const startISO = dayToISO(calendar, pos.startDay);
    // the bar covers [startDay, finishDay) workdays — show the last covered one.
    const finishISO = dayToISO(calendar, Math.max(pos.startDay, pos.finishDay - 1));
    const label = codeName[car.code] || car.code;
    activities[car.carId] = {
      id: car.carId,
      is_new: true,
      in_scope: true,
      name: label,
      task_code: car.code,
      status: "TK_NotStart",
      task_type: "TT_Task",
      duration_days: car.durationDays,
      start: startISO,
      finish: finishISO,
      actual_start: null,
      actual_finish: null,
      drive_start: startISO,
      drive_finish: finishISO,
      complete: false,
      work_code: car.code,
      work_name: label,
      section: "",
      zone: { a: "", b: "", c: "" },
      color: codeColors[car.code] ?? "",
      cstr_type: null,
      cstr_date: null,
    };
    boardDates.set(car.carId, { start: startISO, finish: finishISO });
  }

  const edges: Record<string, DepGraphEdge[]> = {};
  for (const d of train.deps) {
    if (!activities[d.predCarId] || !activities[d.succCarId]) continue;
    (edges[d.succCarId] ??= []).push({
      pred_id: d.predCarId,
      type: d.type,
      lag_hours: d.lagDays * 8,
      edge_id: null,
      is_new: true,
      crosses: false,
      driving: true,
    });
  }

  return {
    graph: { activities, edges, data_date: dayToISO(calendar, 0) },
    boardDates,
  };
}
